import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Surface, PageSection } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Building, Warehouse as WhIcon, PackageOpen } from "lucide-react";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/datetime";

// Branch & Franchise Stock — lazy-loaded per location for large catalogs.
// Nothing loads until the operator picks a Branch / Franchise and clicks Load.
export default function BranchStock() {
  const [branches, setBranches] = useState([]);
  const [selected, setSelected] = useState("");         // branch_id (or "" | "__all__")
  const [committed, setCommitted] = useState(null);     // { branch_id, search }
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/sales/branches")
      .then((r) => setBranches((r.data || []).filter((b) => b.is_active !== false)))
      .catch(() => setBranches([]));
  }, []);

  const load = useCallback(async (params) => {
    setLoading(true);
    try {
      const qp = {};
      if (params.branch_id) qp.branch_id = params.branch_id;
      if (params.search) qp.search = params.search;
      const r = await api.get("/sales/branch-stocks", { params: qp });
      setRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
      setRows([]);
    } finally { setLoading(false); }
  }, []);

  const onLoad = () => {
    if (!selected) return toast.error("Pick a Branch / Franchise first");
    const p = { branch_id: selected, search };
    setCommitted(p);
    load(p);
  };

  const backfill = async () => {
    try {
      const r = await api.post("/sales/invoices/backfill-franchise-stock");
      toast.success(`Backfilled ${r.data.backfilled} franchise invoice(s) · ${r.data.already_present} already present`);
      if (committed) load(committed);
    } catch (e) { toast.error(e?.response?.data?.detail || "Backfill failed"); }
  };

  const onReset = () => {
    setSelected(""); setSearch(""); setRows([]); setCommitted(null);
  };

  const selectedBranch = branches.find((b) => b.id === selected);
  const totalQty = rows.reduce((s, r) => s + Number(r.qty || 0), 0);

  return (
    <div className="space-y-4">
      <PageSection
        title="Branch & Franchise Stock"
        description="Stock currently held at each Branch / Franchise. Pick a location and click Load — nothing runs until you do (kept lazy for large catalogs)."
      >
        <Surface>
          <div className="flex items-end gap-3 flex-wrap">
            <div className="w-72">
              <label className="text-[11px] uppercase tracking-widest text-gray-500 block mb-1">Branch / Franchise *</label>
              <select
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                className="h-9 w-full rounded-md border border-gray-200 text-sm px-2 bg-white"
                data-testid="bs-select"
              >
                <option value="">— Pick a location —</option>
                <optgroup label="Branches">
                  {branches.filter((b) => b.branch_type === "branch").map((b) => (
                    <option key={b.id} value={b.id}>🏢 {b.name}</option>
                  ))}
                </optgroup>
                <optgroup label="Franchises">
                  {branches.filter((b) => b.branch_type === "franchise").map((b) => (
                    <option key={b.id} value={b.id}>🏪 {b.name}</option>
                  ))}
                </optgroup>
              </select>
            </div>
            <div className="flex-1 min-w-[220px] max-w-md">
              <label className="text-[11px] uppercase tracking-widest text-gray-500 block mb-1">Product search (optional)</label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") onLoad(); }}
                  placeholder="Product name or SKU"
                  className="pl-8"
                  data-testid="bs-search"
                />
              </div>
            </div>
            <Button
              className="bg-[#9B1C1C] hover:bg-[#7F1D1D] gap-1.5"
              onClick={onLoad}
              disabled={!selected || loading}
              data-testid="bs-load"
            >
              {loading ? "Loading…" : "Load Stock"}
            </Button>
            <Button variant="outline" onClick={onReset} data-testid="bs-reset">Reset</Button>
            <Button
              variant="outline"
              onClick={backfill}
              className="ml-auto text-xs"
              title="One-time: recompute franchise stock from every approved franchise invoice. Safe to click again."
              data-testid="bs-backfill"
            >
              Backfill franchise invoices
            </Button>
          </div>
          {committed && (
            <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500 flex items-center gap-3 flex-wrap">
              <span>
                Showing <span className="font-semibold text-gray-800">{rows.length}</span> row(s)
                {selectedBranch && (
                  <> for <span className="font-semibold text-gray-800">{selectedBranch.name}</span></>
                )}
              </span>
              {totalQty > 0 && <span>Total units: <span className="font-semibold tabular-nums text-gray-800">{totalQty.toLocaleString("en-IN")}</span></span>}
            </div>
          )}
        </Surface>

        <Surface padded={false}>
          {!committed && (
            <div className="p-16 text-center text-gray-400">
              <PackageOpen className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <div className="font-medium text-gray-500">Select a Branch / Franchise and hit <b>Load Stock</b> to view current holdings.</div>
              <div className="text-xs mt-1">Nothing runs until you click — kept lazy on purpose.</div>
            </div>
          )}
          {committed && (
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                  <th className="px-3 py-2">Product</th>
                  <th className="px-3 py-2">SKU</th>
                  <th className="px-3 py-2">HSN</th>
                  <th className="px-3 py-2">Branch / Franchise</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2">Last Movement</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading && (
                  <tr><td colSpan={6} className="px-3 py-10 text-center text-gray-400">Loading…</td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-10 text-center text-gray-400">
                    <WhIcon className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    No stock rows for this selection.
                  </td></tr>
                )}
                {!loading && rows.map((r, i) => (
                  <tr key={`${r.branch_id}-${r.product_id}-${i}`} className="hover:bg-gray-50" data-testid={`bs-row-${r.product_id}`}>
                    <td className="px-3 py-2 font-medium">{r.product_name}</td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-600">{r.product_sku || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-600">{r.hsn_code || "—"}</td>
                    <td className="px-3 py-2 text-gray-700 flex items-center gap-1"><Building className="h-3.5 w-3.5 text-gray-400" />{r.branch_name || "—"}</td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">{r.qty}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">{formatDateTime(r.last_movement_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Surface>
      </PageSection>
    </div>
  );
}
